import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { ActiveMember, PracticeMember } from '@/lib/members/members.types';
import { useActiveMemberStore } from '@/stores/activeMemberStore';
import { memberInfoByContactIdKey, myPracticeMembersKey, requestMemberInfoByContactId } from './membersApi';

/**
 * The selected member's record, or an idle query when nobody is selected yet.
 *
 * Seeded from the roster row the provider just picked, so the name renders on the same frame as
 * the selection instead of after a round trip. The roster row is already a complete
 * `ActiveMember` today; it is placeholder rather than initial data so the real fetch still runs
 * and the entry doesn't get cached as fresh.
 *
 * The lookup answers a provider token with a null `dateOfBirth` (the API only projects it on the
 * member's own read), while the roster row carries it. The row's value fills that gap so the age
 * doesn't vanish the moment the record replaces the placeholder.
 */
export function useActiveMember() {
  const activeMemberId = useActiveMemberStore((state) => state.activeMemberId);
  const queryClient = useQueryClient();

  const findRosterRow = (): PracticeMember | undefined =>
    queryClient.getQueryData<PracticeMember[]>(myPracticeMembersKey)?.find((member) => member.id === activeMemberId);

  const query = useQuery({
    queryKey: memberInfoByContactIdKey(activeMemberId ?? ''),
    queryFn: async (): Promise<ActiveMember | null> => {
      const member = await requestMemberInfoByContactId(activeMemberId as string);
      if (!member || member.dateOfBirth) return member;
      return { ...member, dateOfBirth: findRosterRow()?.dateOfBirth ?? null };
    },
    enabled: Boolean(activeMemberId),
    placeholderData: (): ActiveMember | undefined => (activeMemberId ? findRosterRow() : undefined),
  });

  return { ...query, activeMemberId };
}
